import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import { CATEGORIES, PRESET_SIZES } from "../lib/constants";
import { toast } from "sonner";
import {
  PenNib, Plus, MagicWand, SignOut, Trash, DotsThree, FilePlus, ArrowsOutSimple, Gear, Crown, SealCheck, GraduationCap, Lightning, PencilSimple, Sparkle, Question, FilmSlate, FolderOpen, Coins, CreditCard,
} from "@phosphor-icons/react";import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { StylesLibraryBrowser } from "../components/StylesLibrary";
import CreativeDirectorManager from "../components/CreativeDirectorManager";
import TopUpModal from "../components/TopUpModal";
import TutorialBanner from "../components/TutorialBanner";
import TutorialVideoManager from "../components/TutorialVideoManager";
import TechnicalDirectorManager from "../components/TechnicalDirectorManager";

const TUTORIAL_DISMISS_KEY = "flyerstudio_tutorial_dismissed_v1";

function DesignThumb({ design: d }) {
  const [broken, setBroken] = useState(false);
  if (d.thumbnail && !broken) {
    return (
      <img
        src={d.thumbnail}
        alt={d.title}
        loading="lazy"
        onError={() => setBroken(true)}
        className="w-full h-full object-cover"
      />
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center" style={{ background: d.background || "#1a1a1a" }}>
      <PenNib size={22} weight="duotone" className="text-white/30" />
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { isPro, openUpgrade, sub: subInfo, refresh: refreshSub } = useSubscription();
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [topupOpen, setTopupOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  // Tutorial banner state — dismiss persists per-browser; auto-hides once the
  // user has ≥1 design (they've clearly figured it out). "?" icon in the header
  // re-opens it by clearing the dismiss flag.
  const [tutorialDismissed, setTutorialDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(TUTORIAL_DISMISS_KEY) === "1";
  });
  const dismissTutorial = () => {
    try { window.localStorage.setItem(TUTORIAL_DISMISS_KEY, "1"); } catch (_) {}
    setTutorialDismissed(true);
  };
  const reopenTutorial = () => {
    try { window.localStorage.removeItem(TUTORIAL_DISMISS_KEY); } catch (_) {}
    setTutorialDismissed(false);
  };
  const [customOpen, setCustomOpen] = useState(false);
  const [cw, setCw] = useState(1080);
  const [ch, setCh] = useState(1080);

  const load = useCallback(async () => {
    try {
      const d = await api.get("/designs");
      setDesigns(d.data);
    } catch (e) {
      toast.error("Failed to load your workspace");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createBlank = async (preset) => {
    try {
      const res = await api.post("/designs", {
        title: `Untitled ${preset.label}`,
        category: preset.category,
        width: preset.width,
        height: preset.height,
        background: "#ffffff",
        elements: [],
      });
      navigate(`/editor/${res.data.id}`);
    } catch (e) {
      toast.error("Could not create design");
    }
  };

  const createCustom = async () => {
    const width = Math.max(50, Math.min(8000, Math.round(Number(cw) || 0)));
    const height = Math.max(50, Math.min(8000, Math.round(Number(ch) || 0)));
    if (!width || !height) {
      toast.error("Enter valid width and height");
      return;
    }
    try {
      const res = await api.post("/designs", {
        title: `Custom ${width} × ${height}`,
        category: "flyer",
        width,
        height,
        background: "#ffffff",
        elements: [],
      });
      setCustomOpen(false);
      navigate(`/editor/${res.data.id}`);
    } catch (e) {
      toast.error("Could not create design");
    }
  };

  const deleteDesign = async (id) => {
    try {
      await api.delete(`/designs/${id}`);
      setDesigns((prev) => prev.filter((d) => d.id !== id));
      toast.success("Design deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-body">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <PenNib weight="fill" size={18} className="text-white" />
            </div>
            <span className="font-display font-extrabold text-lg tracking-tight">FlyerStudio</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/training")} data-testid="nav-training-btn" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full hover:bg-white/5 text-zinc-300 hover:text-white text-sm font-medium transition-colors">
              <GraduationCap weight="fill" size={16} /> <span className="hidden sm:inline">Training</span>
            </button>
            <button onClick={() => navigate("/studio")} data-testid="nav-studio-btn" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full hover:bg-white/5 text-zinc-300 hover:text-white text-sm font-medium transition-colors">
              <MagicWand weight="fill" size={16} className="text-blue-400" /> <span className="hidden sm:inline">AI Studio</span>
            </button>
            <button onClick={() => navigate("/ai-pro-studio")} data-testid="nav-ai-pro-studio-btn" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full hover:bg-white/5 text-zinc-300 hover:text-white text-sm font-medium transition-colors">
              <Sparkle weight="fill" size={16} className="text-fuchsia-400" /> <span className="hidden sm:inline">AI Pro Studio</span>
            </button>
            <button onClick={() => navigate("/library")} data-testid="nav-design-library-btn" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full hover:bg-white/5 text-zinc-300 hover:text-white text-sm font-medium transition-colors">
              <FilmSlate weight="fill" size={16} className="text-amber-300" /> <span className="hidden sm:inline">Library</span>
            </button>
            <button onClick={() => navigate("/workspace")} data-testid="nav-workspace-btn" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full hover:bg-white/5 text-zinc-300 hover:text-white text-sm font-medium transition-colors">
              <FolderOpen weight="fill" size={16} className="text-blue-400" /> <span className="hidden sm:inline">Workspace</span>
            </button>
            {tutorialDismissed && (
              <button
                onClick={reopenTutorial}
                data-testid="tutorial-help-btn"
                title="Watch the 2-minute intro tutorial"
                className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-white/5 text-zinc-400 hover:text-amber-300 transition-colors"
                aria-label="Open tutorial"
              >
                <Question weight="bold" size={18} />
              </button>
            )}
            <span data-testid="dashboard-credit-count" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/10 bg-white/5 text-sm">
              <Lightning weight="fill" size={15} style={{ color: "#FFD60A" }} /> {subInfo?.ai_remaining ?? "—"} <span className="text-zinc-500">credits</span>
            </span>
            <button onClick={() => setTopupOpen(true)} data-testid="dashboard-topup-btn" title="Need more credits? Top-up now" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-black text-sm font-bold transition-transform hover:scale-105" style={{ backgroundColor: "#FFD60A" }}>
              <Lightning weight="fill" size={15} /> <span className="hidden md:inline">Top-Up</span>
            </button>
            {isPro ? (
              <span data-testid="dashboard-pro-badge" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-amber-400/15 text-amber-300 text-xs font-bold">
                <Crown weight="fill" size={14} /> Pro
              </span>
            ) : (
              <button onClick={() => openUpgrade("Unlock watermark-free exports, PDF, share links and more.")} data-testid="dashboard-upgrade-btn" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-amber-400/15 hover:bg-amber-400/25 text-amber-300 text-sm font-semibold transition-colors">
                <Crown weight="fill" size={15} /> Upgrade
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-white/5 transition-colors" data-testid="user-menu-btn">
                  <img
                    src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "U")}&background=3b82f6&color=fff`}
                    alt="me" className="h-8 w-8 rounded-full object-cover"
                  />
                  <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#161616] border-white/10 text-white">
                <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="dashboard-pricing-link" className="cursor-pointer focus:bg-white/10">
                  <Crown size={16} className="mr-2" /> Plans & pricing
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/account/subscription")} data-testid="dashboard-my-subscription-link" className="cursor-pointer focus:bg-white/10">
                  <CreditCard size={16} className="mr-2" /> My subscription
                </DropdownMenuItem>
                {subInfo?.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin/homepage")} data-testid="dashboard-edit-homepage-link" className="cursor-pointer focus:bg-white/10">
                    <PencilSimple size={16} className="mr-2" /> Edit Homepage
                  </DropdownMenuItem>
                )}
                {subInfo?.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin/library")} data-testid="dashboard-manage-library-link" className="cursor-pointer focus:bg-white/10">
                    <FilmSlate size={16} className="mr-2" /> Manage Library
                  </DropdownMenuItem>
                )}
                {subInfo?.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin/billing")} data-testid="dashboard-admin-billing-link" className="cursor-pointer focus:bg-white/10">
                    <Coins size={16} className="mr-2" /> Billing & Credits
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="cursor-pointer focus:bg-white/10">
                  <SignOut size={16} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <TopUpModal open={topupOpen} onClose={() => setTopupOpen(false)} onSuccess={refreshSub} />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-1">
          Hey {user?.name?.split(" ")[0] || "there"} 👋
        </h1>
        <p className="text-zinc-400 mb-8">What will you design today?</p>

        {/* Tutorial banner — visible until dismissed AND until user has ≥1 design.
            Shows extra visual weight ("new" variant) for brand-new accounts. */}
        {!tutorialDismissed && !loading && designs.length === 0 && (
          <TutorialBanner
            onDismiss={dismissTutorial}
            variant={designs.length === 0 ? "new" : "returning"}
          />
        )}

        {/* Start a new design */}
        <section className="mb-12">
          <h2 className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Start a new design</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {PRESET_SIZES.map((p) => (
              <button
                key={p.id}
                onClick={() => createBlank(p)}
                data-testid={`create-${p.id}-btn`}
                className="group rounded-xl border border-white/10 bg-[#121212] p-4 text-left hover:border-blue-500/50 hover:bg-[#161616] transition-all"
              >
                <div className="h-10 w-10 rounded-lg bg-blue-600/15 border border-blue-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <FilePlus weight="fill" size={20} className="text-blue-400" />
                </div>
                <p className="font-semibold text-sm">{p.label}</p>
                <p className="text-xs text-zinc-500">{p.sub}</p>
              </button>
            ))}
            <button
              onClick={() => setCustomOpen(true)}
              data-testid="create-custom-btn"
              className="group rounded-xl border border-dashed border-white/20 bg-[#121212] p-4 text-left hover:border-blue-500/60 hover:bg-[#161616] transition-all"
            >
              <div className="h-10 w-10 rounded-lg bg-fuchsia-600/15 border border-fuchsia-500/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <ArrowsOutSimple weight="bold" size={20} className="text-fuchsia-400" />
              </div>
              <p className="font-semibold text-sm">Custom size</p>
              <p className="text-xs text-zinc-500">Set your own</p>
            </button>
          </div>
        </section>

        {/* My designs */}
        {designs.length > 0 && (
          <section className="mb-12">
            <h2 className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Your designs</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {designs.map((d) => (
                <div key={d.id} data-testid={`design-card-${d.id}`} className="group relative">
                  <button
                    onClick={() => navigate(`/editor/${d.id}`)}
                    className="block w-full rounded-xl overflow-hidden border border-white/10 bg-[#121212] hover:border-blue-500/50 transition-all"
                    style={{ aspectRatio: `${d.width}/${d.height}` }}
                  >
                    <DesignThumb design={d} />
                  </button>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-sm font-medium truncate pr-2">{d.title}</p>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 rounded hover:bg-white/10 text-zinc-400" data-testid={`design-menu-${d.id}`}>
                          <DotsThree weight="bold" size={18} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#161616] border-white/10 text-white">
                        <DropdownMenuItem onClick={() => deleteDesign(d.id)} className="cursor-pointer text-red-400 focus:bg-white/10 focus:text-red-400" data-testid={`delete-design-${d.id}`}>
                          <Trash size={16} className="mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Styles Library (Looks) */}
        <section>
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <h2 className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Styles Library</h2>
            <span className="text-xs text-zinc-500">Pick a look, then generate it in AI Pro Studio</span>
          </div>
          <StylesLibraryBrowser
            admin={!!user?.is_admin}
            manage={!!user?.is_admin}
            selectLabel="Use in AI Pro Studio"
            onSelect={(style) => navigate("/ai-pro-studio", { state: { libraryStyle: style } })}
          />
        </section>

        {/* Creative Director Prompts — admin only */}
        {!!user?.is_admin && (
          <section className="mt-12" data-testid="dashboard-director-section">
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <h2 className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Admin</h2>
              <span className="text-xs text-zinc-500">Presets and site-wide settings — changes go live instantly</span>
            </div>
            <TutorialVideoManager />
            <TechnicalDirectorManager />
            <CreativeDirectorManager />
          </section>
        )}
      </main>


      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="bg-[#141414] border-white/10 text-white sm:max-w-md" data-testid="custom-size-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Custom size</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2 block">Width (px)</label>
              <input
                type="number"
                value={cw}
                onChange={(e) => setCw(e.target.value)}
                data-testid="custom-width-input"
                className="w-full h-10 rounded-lg bg-black/40 border border-white/10 px-3 text-sm text-white outline-none focus:border-blue-500/60"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2 block">Height (px)</label>
              <input
                type="number"
                value={ch}
                onChange={(e) => setCh(e.target.value)}
                data-testid="custom-height-input"
                className="w-full h-10 rounded-lg bg-black/40 border border-white/10 px-3 text-sm text-white outline-none focus:border-blue-500/60"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pb-1">
            {[
              { l: "Square", w: 1080, h: 1080 },
              { l: "A4 Portrait", w: 1240, h: 1754 },
              { l: "Landscape", w: 1920, h: 1080 },
              { l: "Banner", w: 1500, h: 500 },
            ].map((q) => (
              <button
                key={q.l}
                onClick={() => { setCw(q.w); setCh(q.h); }}
                className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs font-medium transition-colors"
              >
                {q.l}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={createCustom} data-testid="custom-create-btn" className="w-full bg-blue-600 hover:bg-blue-500 text-white">
              Create design
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
